import React, { useRef } from 'react';
import { Box, FormHelperText, SxProps, Theme } from '@mui/material';
import Editor, { Monaco } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';

interface PythonEditorProps {
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  height?: string | number;
}

const PythonEditor: React.FC<PythonEditorProps> = ({
  value,
  onChange,
  helperText,
  placeholder, // Monaco Editor doesn't support placeholders natively, but we keep it for compatibility
  disabled = false,
  sx = {},
  height = 400,
}) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

  const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor, monaco: Monaco) => {
    editorRef.current = editor;

    // Configure editor options
    editor.updateOptions({
      fontSize: 14,
      fontFamily: 'Consolas, "Courier New", monospace',
      lineNumbers: 'on',
      minimap: {
        enabled: true,
        scale: 1,
      },
      scrollBeyondLastLine: false,
      automaticLayout: true,
      tabSize: 4,
      insertSpaces: true,
      wordWrap: 'off',
      folding: true, // ✅ Enable code folding
      foldingStrategy: 'indentation', // Fold based on indentation
      showFoldingControls: 'always', // Always show folding controls
      readOnly: disabled,
      scrollbar: {
        vertical: 'auto',
        horizontal: 'auto',
        verticalScrollbarSize: 10,
        horizontalScrollbarSize: 10,
      },
      renderLineHighlight: 'all',
      cursorBlinking: 'smooth',
      smoothScrolling: true,
      contextmenu: true,
      quickSuggestions: true,
      suggestOnTriggerCharacters: true,
      acceptSuggestionOnCommitCharacter: true,
      acceptSuggestionOnEnter: 'on',
      snippetSuggestions: 'inline',
    });

    // Add Python-specific configuration
    monaco.languages.setLanguageConfiguration('python', {
      indentationRules: {
        increaseIndentPattern: /^\s*(def|class|if|elif|else|for|while|try|except|finally|with)\b.*:\s*$/,
        decreaseIndentPattern: /^\s*(return|pass|break|continue|raise)\b.*$/,
      },
    });
  };

  const handleChange = (newValue: string | undefined) => {
    if (newValue !== undefined && newValue !== value) {
      onChange(newValue);
    }
  };

  return (
    <Box sx={{ ...sx, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: disabled ? 'action.disabledBackground' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: disabled ? '#f5f5f5' : '#ffffff',
          flexGrow: 1,
          height: height,
        }}
      >
        <Editor
          height="100%"
          defaultLanguage="python"
          value={value}
          onChange={handleChange}
          onMount={handleEditorDidMount}
          theme="vs" // Use VS Code light theme
          options={{
            readOnly: disabled,
          }}
          loading={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              Chargement de l'éditeur...
            </Box>
          }
        />
      </Box>
      {helperText && (
        <FormHelperText sx={{ ml: 2, mt: 0.5 }}>{helperText}</FormHelperText>
      )}
    </Box>
  );
};

export default PythonEditor;
